import { computed, ref, type ComputedRef } from 'vue'
import {
  searchContextItems,
  type SearchContextType,
  type SearchFunction,
  type SearchFunctionMap,
  type SearchResult
} from '@gat-ship/types'
import { parseContextPrefix } from '../model/global-search-input-parsing'
import type { RecentSearch } from './recent-search-storage'

export const SEARCH_DEBOUNCE_MS = 500

export interface GlobalSearchSessionOptions {
  /** App-injected search functions keyed by context (reactive getter — usually a prop). */
  searchFunctions?: () => SearchFunctionMap | undefined
  /** Built-in providers: part of the global fan-out and the fallback per context. */
  builtInSearchFunctions?: SearchFunctionMap
  /** Invoked when an explicit submit in the default context should be remembered. */
  recordRecentSearch?: (search: RecentSearch) => void
}

export interface GlobalSearchSession {
  defaultSearchContext: SearchContextType
  /** Current input text — always tag-free; writes go through {@link GlobalSearchSession.setInput}. */
  searchInputValue: ComputedRef<string>
  selectedSearchContext: ComputedRef<SearchContextType>
  /** Whether a non-default context is selected (the input shows a tag badge). */
  isTagActive: ComputedRef<boolean>
  isSearchInputEmpty: ComputedRef<boolean>
  /** True through the type-to-search debounce window and while providers run. */
  isSearching: ComputedRef<boolean>
  /**
   * `undefined` while no search has finished; `[]` for a finished
   * search without hits; results otherwise.
   */
  searchResults: ComputedRef<SearchResult[] | undefined>
  isEmptySearchResults: ComputedRef<boolean>
  /**
   * Handles a raw input change: converts a leading context prefix
   * (`c:` …) into a tag, and auto-searches (debounced) in scoped
   * contexts. In the default context searching waits for {@link GlobalSearchSession.submit}.
   */
  setInput: (raw: string) => void
  /** Explicit Enter in the default context: records a recent search and searches immediately. */
  submit: () => void
  /** Selects a context (tag); re-runs the search immediately when input is non-empty. */
  selectContext: (context: SearchContextType) => void
  /** Re-runs a known query in a context (recent searches, "show all" items). */
  runSearch: (query: string, context: SearchContextType) => void
  /** Removes the tag (backspace at input start), keeping the input text. */
  clearTag: () => void
  /** Resets input, tag and results; aborts anything in flight. */
  clearAll: () => void
}

export function createGlobalSearchSession(
  options: GlobalSearchSessionOptions = {}
): GlobalSearchSession {
  const defaultSearchContext = searchContextItems.global

  const _searchInputValue = ref('')
  const _selectedSearchContext = ref<SearchContextType>(defaultSearchContext)
  const _searchResults = ref<SearchResult[] | undefined>(undefined)
  const _isExecutingSearch = ref(false)
  const _isSearchPending = ref(false)

  let activeAbortController: AbortController | null = null
  let debounceTimer: ReturnType<typeof setTimeout> | undefined

  const isSearchInputEmpty = computed(() => _searchInputValue.value === '')

  function getProviders(context: SearchContextType): SearchFunction[] {
    const injected = options.searchFunctions?.() ?? {}
    const builtIn = options.builtInSearchFunctions ?? {}
    if (context === searchContextItems.global) {
      return [builtIn.global, injected.global].filter(
        (searchFunction) => searchFunction !== undefined
      )
    }
    const provider = injected[context] ?? builtIn[context]
    return provider ? [provider] : []
  }

  async function executeSearch(
    query: string,
    context: SearchContextType
  ): Promise<void> {
    cancelPendingSearch()
    activeAbortController?.abort()
    if (query === '') {
      return
    }
    const controller = new AbortController()
    activeAbortController = controller
    _isExecutingSearch.value = true
    try {
      const resultArrays = await Promise.all(
        getProviders(context).map((searchFunction) =>
          searchFunction(query, { signal: controller.signal })
        )
      )
      if (!controller.signal.aborted) {
        _searchResults.value = resultArrays.flat()
      }
    } catch (error) {
      // A superseded search may reject with an abort error — swallow it.
      if (!controller.signal.aborted) {
        throw error
      }
    } finally {
      if (activeAbortController === controller) {
        _isExecutingSearch.value = false
        activeAbortController = null
      }
    }
  }

  function scheduleDebouncedSearch(query: string, context: SearchContextType) {
    cancelPendingSearch()
    _isSearchPending.value = true
    debounceTimer = setTimeout(() => {
      debounceTimer = undefined
      _isSearchPending.value = false
      void executeSearch(query, context)
    }, SEARCH_DEBOUNCE_MS)
  }

  function cancelPendingSearch() {
    if (debounceTimer !== undefined) {
      clearTimeout(debounceTimer)
      debounceTimer = undefined
    }
    _isSearchPending.value = false
  }

  function setInput(raw: string) {
    _searchResults.value = undefined
    cancelPendingSearch()
    const parsed = parseContextPrefix(raw)
    if (parsed) {
      _selectedSearchContext.value = parsed.context
      _searchInputValue.value = parsed.remainder
    } else {
      _searchInputValue.value = raw
    }
    const context = _selectedSearchContext.value
    if (context === defaultSearchContext) {
      // The default context searches on explicit submit only.
      return
    }
    if (_searchInputValue.value === '') {
      return
    }
    scheduleDebouncedSearch(_searchInputValue.value, context)
  }

  function submit() {
    if (isSearchInputEmpty.value) {
      return
    }
    if (_selectedSearchContext.value !== defaultSearchContext) {
      // Scoped contexts already search as the user types.
      return
    }
    options.recordRecentSearch?.({
      searchString: _searchInputValue.value,
      context: _selectedSearchContext.value,
      timestamp: Date.now()
    })
    void executeSearch(_searchInputValue.value, _selectedSearchContext.value)
  }

  function selectContext(context: SearchContextType) {
    cancelPendingSearch()
    _selectedSearchContext.value = context
    _searchResults.value = undefined
    if (!isSearchInputEmpty.value) {
      void executeSearch(_searchInputValue.value, context)
    }
  }

  function runSearch(query: string, context: SearchContextType) {
    _searchInputValue.value = query
    selectContext(context)
  }

  function clearTag() {
    if (_selectedSearchContext.value === defaultSearchContext) {
      return
    }
    selectContext(defaultSearchContext)
  }

  function clearAll() {
    cancelPendingSearch()
    activeAbortController?.abort()
    activeAbortController = null
    _isExecutingSearch.value = false
    _searchInputValue.value = ''
    _selectedSearchContext.value = defaultSearchContext
    _searchResults.value = undefined
  }

  return {
    defaultSearchContext,
    searchInputValue: computed(() => _searchInputValue.value),
    selectedSearchContext: computed(() => _selectedSearchContext.value),
    isTagActive: computed(
      () => _selectedSearchContext.value !== defaultSearchContext
    ),
    isSearchInputEmpty,
    isSearching: computed(
      () => _isSearchPending.value || _isExecutingSearch.value
    ),
    searchResults: computed(() => _searchResults.value),
    isEmptySearchResults: computed(
      () =>
        Array.isArray(_searchResults.value) && _searchResults.value.length === 0
    ),
    setInput,
    submit,
    selectContext,
    runSearch,
    clearTag,
    clearAll
  }
}
